// KodeBarang.jsx
import { useEffect, useState, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ReloadPage from "../utils/func/ReloadPage";

const JSON_URL = "/gnr_item_108.json"; // file di folder public

export default function KodeBarang() {
  const [data, setData] = useState([]);
  const [parentCodes, setParentCodes] = useState(new Set()); // kode yang punya anak
  const [jenis, setJenis] = useState([]);
  const [objek, setObjek] = useState([]);
  const [rincian, setRincian] = useState([]);
  const [subrincian, setSubrincian] = useState([]);
  const [subsubrincian, setSubsubrincian] = useState([]);
  const [barang, setBarang] = useState([]);

  const [selectedJenis, setSelectedJenis] = useState("");
  const [selectedObjek, setSelectedObjek] = useState("");
  const [selectedRincian, setSelectedRincian] = useState("");
  const [selectedSubrincian, setSelectedSubrincian] = useState("");
  const [selectedSubsubrincian, setSelectedSubsubrincian] = useState("");

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 1000;

  const [loading, setLoading] = useState(true);

  // --- Load JSON dan hitung parentCodes ---
  useEffect(() => {
    setLoading(true);
    fetch(JSON_URL)
      .then((res) => {
        if (!res.ok) throw new Error("Gagal memuat JSON");
        return res.json();
      })
      .then((json) => {
        setData(json || []);

        // hitung parent codes: setiap kode yang merupakan ancestor dari kode lain dianggap punya anak
        const parents = new Set();
        (json || []).forEach((it) => {
          const kode = it?.Kode;
          if (!kode) return;
          const parts = kode.split(".");
          // tambahkan semua ancestor (bukan dirinya) ke set parents
          for (let i = 1; i < parts.length; i++) {
            const ancestor = parts.slice(0, i).join(".");
            parents.add(ancestor);
          }
        });
        setParentCodes(parents);

        // Ambil level "Jenis" (kode dengan 2 segmen misal "1.1")
        const jenisData = (json || []).filter(
          (i) => i?.Kode && i.Kode.split(".").length === 2
        );
        setJenis(jenisData);
      })
      .catch((err) => {
        console.error("Fetch error:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  // helper: ambil immediate children (level +1) dari prefix
  const immediateChildren = (prefix) => {
    const level = prefix.split(".").length;
    return data.filter(
      (i) => i?.Kode && i.Kode.startsWith(prefix + ".") && i.Kode.split(".").length === level + 1
    );
  };

  // helper: ambil semua descendant (semua level lebih dalam) dari prefix
  const allDescendants = (prefix) => {
    return data.filter((i) => i?.Kode && i.Kode.startsWith(prefix + "."));
  };

  // --- Saat pilih Jenis: isi dropdown Objek (immediate children), reset deeper selects ---
  useEffect(() => {
    setSelectedObjek("");
    setSelectedRincian("");
    setSelectedSubrincian("");
    setSelectedSubsubrincian("");
    setObjek([]);
    setRincian([]);
    setSubrincian([]);
    setSubsubrincian([]);
    setBarang([]);

    if (selectedJenis) {
      const objs = immediateChildren(selectedJenis);
      setObjek(objs);

      // jika mau menampilkan barang langsung dari jenis: ambil semua descendant, ambil yang leaf saja
      // tapi default kita tidak menampilkan barang sampai user pilih Objek
      // jika ingin auto tampil, uncomment berikut:
      // const desc = allDescendants(selectedJenis);
      // setBarang(desc.filter((i) => !parentCodes.has(i.Kode)));
    }
  }, [selectedJenis, data, parentCodes]);

  // --- Saat pilih Objek: isi Rincian (immediate children) dan set barang = semua descendant yang leaf ---
  useEffect(() => {
    setSelectedRincian("");
    setSelectedSubrincian("");
    setSelectedSubsubrincian("");
    setRincian([]);
    setSubrincian([]);
    setSubsubrincian([]);
    setBarang([]);

    if (selectedObjek) {
      const rinc = immediateChildren(selectedObjek);
      setRincian(rinc);

      const desc = allDescendants(selectedObjek);
      // leaf = descendant yang bukan parent
      const leaves = desc.filter((i) => !parentCodes.has(i.Kode));
      setBarang(leaves);
    }
  }, [selectedObjek, data, parentCodes]);

  // --- Saat pilih Rincian: isi SubRincian (immediate children) dan set barang = semua descendant yang leaf ---
  useEffect(() => {
    setSelectedSubrincian("");
    setSelectedSubsubrincian("");
    setSubrincian([]);
    setSubsubrincian([]);
    setBarang([]);

    if (selectedRincian) {
      const subr = immediateChildren(selectedRincian);
      setSubrincian(subr);

      const desc = allDescendants(selectedRincian);
      const leaves = desc.filter((i) => !parentCodes.has(i.Kode));
      setBarang(leaves);
    }
  }, [selectedRincian, data, parentCodes]);

  // --- Saat pilih SubRincian: isi SubSubRincian (immediate children) dan set barang = all descendant leaf ---
  useEffect(() => {
    setSelectedSubsubrincian("");
    setSubsubrincian([]);
    setBarang([]);

    if (selectedSubrincian) {
      const ssr = immediateChildren(selectedSubrincian);
      setSubsubrincian(ssr);

      const desc = allDescendants(selectedSubrincian);
      const leaves = desc.filter((i) => !parentCodes.has(i.Kode));
      setBarang(leaves);
    }
  }, [selectedSubrincian, data, parentCodes]);

  // --- Saat pilih SubSubRincian: tampilkan semua child leaf di bawahnya ---
  useEffect(() => {
    setBarang([]);
    if (selectedSubsubrincian) {
      const desc = allDescendants(selectedSubsubrincian);
      const leaves = desc.filter((i) => !parentCodes.has(i.Kode));
      setBarang(leaves);
    }
  }, [selectedSubsubrincian, data, parentCodes]);

  // --- Filter pencarian berdasarkan barang (yang sudah leaf) ---
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return barang.filter(
      (b) =>
        (!q ||
          (b?.Kode || "").toLowerCase().includes(q) ||
          (b?.Nama || "").toLowerCase().includes(q))
    );
  }, [search, barang]);

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const paginated = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page]);

  // Loader saat fetch
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-600 mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Memuat data kode barang, harap tunggu...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
        {/* Filter Cascading */}
        <div className="flex flex-col gap-4 w-full border p-4 rounded-2xl bg-stone-100">
          {/* Jenis */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center text-xs">
            <p className="text-xs w-[120px]">Jenis</p>
            <Select
              onValueChange={(v) => {
                setSelectedJenis(v);
                setSelectedObjek("");
                setSelectedRincian("");
                setSelectedSubrincian("");
                setSelectedSubsubrincian("");
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full text-xs">
                <SelectValue placeholder="Pilih Jenis Aset" />
              </SelectTrigger>
              <SelectContent>
                {jenis.map((j) => (
                  <SelectItem key={j.Kode} value={j.Kode} className="text-xs">
                    {j.Kode} - {j.Nama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Objek */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center text-xs">
            <p className="text-xs w-[120px]">Objek</p>
            <Select
              onValueChange={(v) => {
                setSelectedObjek(v);
                setSelectedRincian("");
                setSelectedSubrincian("");
                setSelectedSubsubrincian("");
                setPage(1);
              }}
              disabled={!objek.length}
            >
              <SelectTrigger className="w-full text-xs">
                <SelectValue placeholder="Pilih Objek" />
              </SelectTrigger>
              <SelectContent>
                {objek.map((o) => (
                  <SelectItem key={o.Kode} value={o.Kode} className="text-xs">
                    {o.Kode} - {o.Nama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Rincian */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center text-xs">
            <p className="text-xs w-[120px]">Rincian</p>
            <Select
              onValueChange={(v) => {
                setSelectedRincian(v);
                setSelectedSubrincian("");
                setSelectedSubsubrincian("");
                setPage(1);
              }}
              disabled={!rincian.length}
            >
              <SelectTrigger className="w-full text-xs">
                <SelectValue placeholder="Pilih Rincian Objek" />
              </SelectTrigger>
              <SelectContent>
                {rincian.map((r) => (
                  <SelectItem key={r.Kode} value={r.Kode} className="text-xs">
                    {r.Kode} - {r.Nama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sub Rincian */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center text-xs">
            <p className="text-xs w-[120px]">Sub Rincian</p>
            <Select
              onValueChange={(v) => {
                setSelectedSubrincian(v);
                setSelectedSubsubrincian("");
                setPage(1);
              }}
              disabled={!subrincian.length}
            >
              <SelectTrigger className="w-full text-xs">
                <SelectValue placeholder="Pilih Sub Rincian" />
              </SelectTrigger>
              <SelectContent>
                {subrincian.map((sr) => (
                  <SelectItem key={sr.Kode} value={sr.Kode} className="text-xs">
                    {sr.Kode} - {sr.Nama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sub Sub Rincian */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center text-xs">
            <p className="text-xs w-[120px]">Sub Sub Rincian</p>
            <Select
                onValueChange={(v) => {
                  setSelectedSubsubrincian(v);
                  setPage(1);
                }}
                disabled={!subsubrincian.length}
              >
              <SelectTrigger className="w-full text-xs">
                <SelectValue placeholder="Pilih Sub Sub Rincian" />
              </SelectTrigger>
              <SelectContent>
                {subsubrincian.map((ssr) => (
                  <SelectItem key={ssr.Kode} value={ssr.Kode} className="text-xs">
                    {ssr.Kode} - {ssr.Nama}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <ReloadPage />
        </div>

        {/* Search */}
        <div className="flex flex-col gap-2 w-full py-4">
          <Input
            placeholder="Cari kode / nama barang..."
            type="search"
            className='shadow-xl'
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
          <p className="text-xs opacity-70 text-red-500">*hover/sorot pada kode atau nama barang yang tampil di bawah</p>
        </div>

        {/* Table */}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">Kode Barang</TableHead>
              <TableHead className="text-xs">Nama Barang</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginated.length > 0 ? (
              paginated.map((b, idx) => (
                <TableRow key={idx}>
                  <TableCell className="text-xs">
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help">{b.Kode}</span>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs">
                          {(() => {
                            const kodeParts = b.Kode.split(".");
                            const path = [];
                            for (let i = 1; i <= kodeParts.length; i++) {
                              const prefix = kodeParts.slice(0, i).join(".");
                              const found = data.find((d) => d.Kode === prefix);
                              if (found && found.Nama) path.push(found.Nama);
                            }
                            return path.length > 0 ? path.join(" → ") : "Tidak ada detail";
                          })()}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>

                  <TableCell className="text-xs">
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-col gap-1">
                            <span className="cursor-help font-bold text-lg">{b.Nama}</span>
                            <p className="text-[10px] font-light whitespace-normal bg-stone-100 py-1 px-2 rounded-md w-fit border border-dashed border border-stone-300">
                              {(() => {
                                const kodeParts = b.Kode.split(".");
                                const path = [];
                                for (let i = 1; i <= kodeParts.length; i++) {
                                  const prefix = kodeParts.slice(0, i).join(".");
                                  const found = data.find((d) => d.Kode === prefix);
                                  if (found && found.Nama) path.push(found.Nama);
                                }
                                return path.length > 0 ? path.join(" → ") : "Tidak ada detail";
                              })()}
                            </p>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs">
                          {(() => {
                            const kodeParts = b.Kode.split(".");
                            const path = [];
                            for (let i = 1; i <= kodeParts.length; i++) {
                              const prefix = kodeParts.slice(0, i).join(".");
                              const found = data.find((d) => d.Kode === prefix);
                              if (found && found.Nama) path.push(found.Nama);
                            }
                            return path.length > 0 ? path.join(" → ") : "Tidak ada detail";
                          })()}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                </TableRow>

              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} className="text-center text-gray-500 text-xs">
                  Data tidak ditemukan
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Label jumlah data */}
        <div className="mt-2 text-xs text-gray-600">
          Menampilkan {paginated.length.toLocaleString("id-ID")} dari {filtered.length.toLocaleString("id-ID")} data
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <span className="text-xs opacity-50">Halaman {page} dari {totalPages || 1}</span>
          <div className="space-x-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1}>Prev</Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(p + 1, totalPages))} disabled={page === totalPages || totalPages === 0}>Next</Button>
          </div>
        </div>
    </div>
  );
}